import {
  CheckOutlined,
  CustomerServiceOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { Menu } from '@headlessui/react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import {
  getNotiHistory,
  NotificationHistoryItem,
  openNotiHistoryModal,
  openOrderDetailFromNoti,
  readAllNotis,
  readNoti
} from '@slice/orderManager.slice';
import { Empty, Spin, Tooltip } from 'antd';
import { RootState } from 'app/store';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;

// shortcut: đoán tag phiếu chứa "Ticket" (vd SupportTicket), sửa khi backend chốt tên tag
export const isTicketTag = (tag?: string) => /ticket/i.test(tag || '');

const TYPE_ICON: Record<string, { Icon: any; color: string }> = {
  Order: { Icon: ShoppingCartOutlined, color: 'text-green-400' },
  ProductTransaction: { Icon: SwapOutlined, color: 'text-yellow-400' },
  ticket: { Icon: CustomerServiceOutlined, color: 'text-blue-300' }
};

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(iso));

export default function NotificationHistory() {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  const { items, hasNextPage, pageIndex, loading, loadingMore, unreadCount } = useSelector(
    (state: RootState) => state.orderManager.notiHistory!
  );

  // Tải lần đầu khi mở panel.
  // Ngoài ra, khi FCM đẩy thông báo mới, gọi lại dispatch(getNotiHistory({ pageIndex: 1 }))
  // từ chỗ lắng nghe FCM của bạn để list luôn mới.
  useEffect(() => {
    dispatch(getNotiHistory({ pageIndex: 1, pageSize: PAGE_SIZE }));
  }, [dispatch]);

  // Off trang -> điều hướng. Đang ở trang QLĐH -> gọi api/Order/GetDetail lấy orderType.id
  // rồi truyền vào getDetailOfOrder (xử lý trong thunk openOrderDetailFromNoti).
  // Modal chi tiết đơn nằm ở Layout (dùng chung mọi trang) nên chuyển trang xong mở luôn,
  // trang QLĐH không đọc ?maDonHang
  const handleDetail = (maDonHang: string) => {
    if (window.location.pathname !== '/quan-ly-don-hang') navigate('/quan-ly-don-hang');
    dispatch(openOrderDetailFromNoti(maDonHang));
  };

  const openTransaction = (transactionId: string) => {
    // TODO: điều hướng tới chi tiết lệnh giao dịch theo id.
    // Lưu ý: lệnh đã hủy => giao dịch đã bị xóa, cần xử lý trường hợp không tìm thấy.
    // Ví dụ: navigate(`/quan-ly-giao-dich?id=${transactionId}`);
    console.warn('Chưa xử lý mở chi tiết ProductTransaction:', transactionId);
  };

  const handleClickIdentifier = (item: NotificationHistoryItem, identifier: string) => {
    dispatch(readNoti(item.id)); // đánh dấu đã đọc

    if (item.tag === 'Order') handleDetail(identifier);
    // Phiếu: identifier là id phiếu (vd 138), TicketManager đọc ?ticketId rồi mở phiếu
    else if (isTicketTag(item.tag)) navigate(`/quan-ly-phieu?ticketId=${identifier}`);
    else if (item.tag === 'ProductTransaction') openTransaction(identifier);
  };

  const handleReadAll = () => {
    if (unreadCount > 0) dispatch(readAllNotis());
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasNextPage) {
      dispatch(getNotiHistory({ pageIndex: pageIndex + 1, pageSize: PAGE_SIZE }));
    }
  };

  return (
    <div className="ffs-text flex flex-col px-1 pb-2 max-h-[60vh] w-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between pb-2">
        <div className="font-semibold !text-sm">
          <span>{'Danh sách'}</span>
          {/* {unreadCount > 0 && <span className="ml-1 text-red-500">({unreadCount})</span>} */}
        </div>

        <div className="flex items-center gap-3">
          {items.length > 0 && unreadCount > 0 && (
            <Tooltip placement="topLeft" title={t('Notification.ReadAll')}>
              <div
                className="flex cursor-pointer items-center gap-1 text-xs hover:text-orange-400"
                onClick={handleReadAll}
              >
                <CheckOutlined rev={''} />
                {t('Notification.ReadAll')}
              </div>
            </Tooltip>
          )}
          {/* Xem tất cả -> mở modal bảng phân trang */}
          {/* <div
            className="cursor-pointer text-xs underline hover:text-orange-400"
            onClick={() => dispatch(openNotiHistoryModal())}
          >
            {t('Notification.ViewAll')}
          </div> */}
        </div>
      </div>

      {/* Loading trang đầu */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <div className="py-6">
          <Empty description={t('Notification.Empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <div className="flex flex-col gap-1 pt-2">
          {items.map(item => {
            const isTicket = isTicketTag(item.tag);
            const ids = item.identifiers || [];
            const { Icon, color } = isTicket ? TYPE_ICON.ticket : TYPE_ICON[item.tag] || TYPE_ICON.Order;
            return (
              // Dòng không bấm được để bôi đen copy; chỉ nút mã mới chuyển trang
              <div
                key={item.id}
                className={`flex select-text gap-3 rounded-md px-2.5 py-2.5 transition-colors hover:bg-white/10 ${
                  item.isRead ? '' : 'bg-white/5'
                }`}
              >
                <Icon rev={''} className={`mt-0.5 text-base ${color} ${item.isRead ? 'opacity-50' : ''}`} />

                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <div
                      className={`flex-1 text-[13px] leading-snug ${
                        item.isRead ? 'text-white/70' : 'font-semibold text-white'
                      }`}
                    >
                      {item.title}
                    </div>
                    {!item.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-500" />}
                  </div>

                  {item.body && <div className="mt-0.5 text-xs leading-relaxed text-white/60">{item.body}</div>}

                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[11px] text-white/50">{formatDate(item.createDate)}</span>

                    {/* Mỗi identifier 1 nút; phiếu: identifier là id (vô nghĩa với người dùng) nên ghi "Mở phiếu" */}
                    {ids.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {ids.map(identifier => (
                          // Menu.Item: bấm xong dropdown (Headless UI) tự đóng, chỉ còn trang đích
                          <Menu.Item
                            as="button"
                            key={identifier}
                            type="button"
                            onClick={() => handleClickIdentifier(item, identifier)}
                            className="flex items-center gap-1 rounded bg-white/10 px-2 py-0.5 text-xs text-white transition-colors hover:bg-orange-500"
                          >
                            {isTicket ? (ids.length > 1 ? `Phiếu ${identifier}` : 'Mở phiếu') : identifier}
                            <RightOutlined rev={''} className="text-[9px]" />
                          </Menu.Item>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Load more */}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="text-xs text-gray-300 hover:text-white disabled:opacity-50"
              >
                {loadingMore ? <Spin size="small" /> : t('Notification.LoadMore')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
